import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, RotateCcw, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function DeletedPeople() {
  const navigate = useNavigate();
  const [data, setData] = useState({ persons: [], total: 0, page: 1, page_size: 25 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [viewing, setViewing] = useState(null);
  const [restoreReason, setRestoreReason] = useState("");
  const openView = async (id) => { try { const { data } = await api.get(`/persons/deleted/${id}`); setRestoreReason(""); setViewing(data); } catch (e) { toast.error(apiError(e)); } };
  const daysAgo = (iso) => { if (!iso) return ""; const d = Math.floor((Date.now() - new Date(iso)) / 86400000); return d <= 0 ? "today" : `${d} day${d === 1 ? "" : "s"} ago`; };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/persons/deleted", { params: { q, page, page_size: 25 } }); setData(data); setSel({}); }
    catch (e) { toast.error(apiError(e)); if (e?.response?.status === 403) navigate("/people"); }
    finally { setLoading(false); }
  }, [q, page, navigate]);
  useEffect(() => { load(); }, [load]);

  const restore = async (id, reason) => { try { await api.post(`/persons/${id}/restore`, { reason: reason || null }); toast.success("Person restored"); load(); } catch (e) { toast.error(apiError(e)); } };
  const restoreBulk = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) return;
    try { const { data } = await api.post("/persons/restore-bulk", { ids }); toast.success(`Restored ${data.restored}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const selectedCount = Object.values(sel).filter(Boolean).length;

  const columns = [
    { key: "sel", header: "", render: (r) => <Checkbox checked={!!sel[r.id]} onCheckedChange={(v) => setSel({ ...sel, [r.id]: v })} data-testid={`del-select-${r.id}`} onClick={(e) => e.stopPropagation()} /> },
    { key: "name", header: "Person", render: (r) => (
      <div><p className="font-medium text-slate-800">{r.first_name} {r.last_name}</p><p className="text-xs text-slate-400 font-mono">{r.human_id}</p></div>) },
    { key: "class", header: "Classifications", render: (r) => <div className="flex flex-wrap gap-1">{(r.classifications || []).map((c) => <StatusBadge key={c} status={c} />) || "—"}</div> },
    { key: "org", header: "Organization", render: (r) => r.organization_name || <span className="text-slate-300">—</span> },
    { key: "recruiter", header: "Recruiter", render: (r) => r.recruiter_name || <span className="text-slate-300">—</span> },
    { key: "deleted_by", header: "Deleted By", render: (r) => r.deleted_by_name || <span className="text-slate-300">—</span> },
    { key: "reason", header: "Reason", render: (r) => r.delete_reason ? <span className="text-xs text-slate-600">{r.delete_reason}</span> : <span className="text-slate-300">—</span> },
    { key: "deleted_at", header: "Deleted", render: (r) => <span className="text-xs text-slate-500 whitespace-nowrap">{r.deleted_at ? new Date(r.deleted_at).toLocaleDateString() : "—"}<br /><span className="text-slate-400">{daysAgo(r.deleted_at)}</span></span> },
    { key: "action", header: "", render: (r) => (
      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); restore(r.id); }} data-testid={`del-restore-${r.id}`}><RotateCcw className="w-4 h-4 mr-1.5" /> Restore</Button>) },
  ];

  return (
    <div>
      <PageHeader title="Deleted People" description="Soft-deleted people. Restore returns the record with the same Person ID and all related data intact."
        actions={selectedCount > 0 && <Button onClick={restoreBulk} data-testid="bulk-restore" className="bg-[#006090] hover:bg-[#004E75]"><RotateCcw className="w-4 h-4 mr-1.5" /> Restore {selectedCount}</Button>} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/people")} data-testid="back-to-people">All People</Button>
      </div>
      <div className="relative mb-4 max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="deleted-search" placeholder="Search name, email, ID…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" />
      </div>
      <DataTable columns={columns} rows={data.persons} loading={loading}
        onRowClick={(r) => openView(r.id)}
        page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage}
        empty={<EmptyState icon={Trash2} title="No deleted people" description="Deleted people will appear here and can be restored." />}
        testId="deleted-people-table" />
      {viewing && (
        <Dialog open onOpenChange={(o) => !o && setViewing(null)}>
          <DialogContent className="bg-white" data-testid="deleted-person-view">
            <DialogHeader><DialogTitle>{viewing.first_name} {viewing.last_name} · <span className="font-mono text-sm text-slate-400">{viewing.human_id}</span></DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[["Email", viewing.email], ["Mobile", viewing.mobile || viewing.phone], ["Organization", viewing.organization_name],
                ["Classifications", (viewing.classifications || []).join(", ")], ["Opportunities", (viewing.opportunities || []).length],
                ["Deleted", viewing.deleted_at ? new Date(viewing.deleted_at).toLocaleString() : "—"]].map(([k, v]) => (
                <div key={k}><p className="text-xs font-semibold text-slate-500">{k}</p><p className="text-slate-800 mt-0.5">{v || "—"}</p></div>
              ))}
            </div>
            <p className="text-xs text-slate-400">Read-only recovery view. Restoring keeps the same Person ID and all related data.</p>
            <div>
              <label className="text-xs font-semibold text-slate-500">Restore reason (optional)</label>
              <Input value={restoreReason} onChange={(e) => setRestoreReason(e.target.value)} className="mt-1" placeholder="e.g. Deleted in error" data-testid="restore-reason" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
              <Button onClick={() => { restore(viewing.id, restoreReason); setViewing(null); }} data-testid="view-restore" className="bg-[#006090] hover:bg-[#004E75]"><RotateCcw className="w-4 h-4 mr-1.5" /> Restore</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
